import React, { createContext, useContext, useReducer, useMemo, useEffect } from 'react';
import { Cohort } from 'App.types';

interface CohortsState {
  cohorts: Cohort[];
  loading: boolean;
  error?: Error;
  selectedRegion: string;
}

type CohortsAction =
  | { type: 'SET_COHORTS'; payload: Cohort[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: Error | undefined }
  | { type: 'ADD_COHORT'; payload: Cohort }
  | { type: 'REMOVE_COHORT'; payload: string }
  | { type: 'UPDATE_COHORT'; payload: { id: string; updates: Partial<Cohort> } }
  | { type: 'SET_REGION'; payload: string };

const initialState: CohortsState = {
  cohorts: [],
  loading: true,
  error: undefined,
  selectedRegion: sessionStorage.getItem('selectedRegion') || 'us-east-1',
};

function cohortsReducer(state: CohortsState, action: CohortsAction): CohortsState {
  switch (action.type) {
    case 'SET_COHORTS':
      return {
        ...state,
        cohorts: action.payload,
        loading: false,
        error: undefined,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'ADD_COHORT':
      return { ...state, cohorts: [...state.cohorts, action.payload] };
    case 'REMOVE_COHORT':
      return { ...state, cohorts: state.cohorts.filter((c) => c.id !== action.payload) };
    case 'UPDATE_COHORT':
      return {
        ...state,
        cohorts: state.cohorts.map((c) =>
          c.id === action.payload.id ? { ...c, ...action.payload.updates } : c,
        ),
      };
    case 'SET_REGION':
      return { ...state, selectedRegion: action.payload };
    default:
      return state;
  }
}

interface CohortsContextValue {
  cohorts: Cohort[];
  loading: boolean;
  error?: Error;
  selectedRegion: string;
  dispatch: React.Dispatch<CohortsAction>;
}

const CohortsContext = createContext<CohortsContextValue | null>(null);

export const useCohortsContext = () => {
  const context = useContext(CohortsContext);
  if (!context) {
    throw new Error('useCohortsContext must be used within CohortsContextProvider');
  }
  return context;
};

export const CohortsContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cohortsReducer, initialState);

  useEffect(() => {
    sessionStorage.setItem('selectedRegion', state.selectedRegion);
  }, [state.selectedRegion]);

  const contextValue: CohortsContextValue = useMemo(
    () => ({
      cohorts: state.cohorts,
      loading: state.loading,
      error: state.error,
      selectedRegion: state.selectedRegion,
      dispatch,
    }),
    [state.cohorts, state.loading, state.error, state.selectedRegion],
  );

  return <CohortsContext.Provider value={contextValue}>{children}</CohortsContext.Provider>;
};

export default CohortsContextProvider;
